/**
 * Default object catalog and inventory stock limits.
 *
 * IMPORTANT — PLACEHOLDER DATA:
 * Seat counts and dimensions follow the PRD's documented defaults (round
 * table seats 8, max 9; 6' rect seats 6; 8' rect seats 8). Inventory stock
 * quantities for round/rectangle tables are PLACEHOLDERS — the PRD (§6.2,
 * §17) flags that real venue stock counts must be confirmed by the venue
 * owner before launch. Chair count (200) and dance floor quantities (1 each)
 * were explicitly provided and are real.
 *
 * These rows seed the `object_catalog` and `inventory_limits` tables on
 * first migration. After seeding, the database is the source of truth —
 * Admins edit catalog/inventory data through the app, not this file.
 */

import type { ObjectCatalogEntry, InventoryLimit } from './types';

// ---------------------------------------------------------------------------
// New object types added in production update milestone
// ---------------------------------------------------------------------------

/**
 * Arbor dimensions — isolated constants so venue can confirm/adjust easily.
 * 6ft wide × 2ft deep is a standard ceremony arch footprint.
 * The arbor is a placement marker only — no stock limit.
 * ⚠️  CONFIRM WITH VENUE before treating as final.
 */
export const ARBOR_WIDTH_FT = 6;
export const ARBOR_DEPTH_FT = 2;

export const DEFAULT_OBJECT_CATALOG: ObjectCatalogEntry[] = [
  {
    itemType: 'round_table',
    displayName: 'Round Table',
    category: 'guest_seating',
    defaultSeats: 8,
    maxSeats: 9,
    defaultWidthFt: 5,
    defaultHeightFt: 5,
    iconKey: 'round_table',
    hasStockLimit: true,
  },
  {
    itemType: 'rect_table_6',
    displayName: "6' Rectangle Table",
    category: 'guest_seating',
    defaultSeats: 6,
    maxSeats: 8,
    defaultWidthFt: 6,
    defaultHeightFt: 2.5,
    iconKey: 'rect_table',
    hasStockLimit: true,
  },
  {
    itemType: 'rect_table_8',
    displayName: "8' Rectangle Table",
    category: 'guest_seating',
    defaultSeats: 8,
    maxSeats: 10,
    defaultWidthFt: 8,
    defaultHeightFt: 2.5,
    iconKey: 'rect_table',
    hasStockLimit: true,
  },
  {
    itemType: 'head_table',
    displayName: 'Head Table',
    category: 'guest_seating',
    defaultSeats: 6,
    maxSeats: 16,
    defaultWidthFt: 8,
    defaultHeightFt: 2.5,
    iconKey: 'head_table',
    hasStockLimit: false,
  },
  {
    itemType: 'sweetheart_table',
    displayName: 'Sweetheart Table',
    category: 'guest_seating',
    defaultSeats: 2,
    maxSeats: 2,
    defaultWidthFt: 3,
    defaultHeightFt: 2.5,
    iconKey: 'sweetheart_table',
    hasStockLimit: false,
  },
  {
    itemType: 'dance_floor_15',
    displayName: "Dance Floor (15' x 15')",
    category: 'activity_zone',
    defaultSeats: null,
    maxSeats: null,
    defaultWidthFt: 15,
    defaultHeightFt: 15,
    iconKey: 'dance_floor',
    hasStockLimit: true,
  },
  {
    itemType: 'dance_floor_18',
    displayName: "Dance Floor (18' x 18')",
    category: 'activity_zone',
    defaultSeats: null,
    maxSeats: null,
    defaultWidthFt: 18,
    defaultHeightFt: 18,
    iconKey: 'dance_floor',
    hasStockLimit: true,
  },
  {
    itemType: 'dj_booth',
    displayName: 'DJ Setup Area',
    category: 'activity_zone',
    defaultSeats: null,
    maxSeats: null,
    defaultWidthFt: 6,
    defaultHeightFt: 4,
    iconKey: 'dj_booth',
    hasStockLimit: false,
  },
  {
    itemType: 'cake_table',
    displayName: 'Cake Table',
    category: 'feature_zone',
    defaultSeats: null,
    maxSeats: null,
    defaultWidthFt: 3.5,
    defaultHeightFt: 3.5,
    iconKey: 'cake_table',
    hasStockLimit: false,
  },
  {
    itemType: 'gift_table',
    displayName: 'Gift Table',
    category: 'feature_zone',
    defaultSeats: null,
    maxSeats: null,
    defaultWidthFt: 6,
    defaultHeightFt: 2.5,
    iconKey: 'gift_table',
    hasStockLimit: false,
  },
  {
    itemType: 'dessert_station',
    displayName: 'Dessert Station',
    category: 'feature_zone',
    defaultSeats: null,
    maxSeats: null,
    defaultWidthFt: 6,
    defaultHeightFt: 2.5,
    iconKey: 'dessert_station',
    hasStockLimit: false,
  },
  {
    itemType: 'donut_wall',
    displayName: 'Donut Wall',
    category: 'feature_zone',
    defaultSeats: null,
    maxSeats: null,
    defaultWidthFt: 6,
    defaultHeightFt: 1.5,
    iconKey: 'donut_wall',
    hasStockLimit: false,
  },
  {
    itemType: 'photo_booth',
    displayName: 'Photo Booth Area',
    category: 'feature_zone',
    defaultSeats: null,
    maxSeats: null,
    defaultWidthFt: 7,
    defaultHeightFt: 7,
    iconKey: 'photo_booth',
    hasStockLimit: false,
  },
  {
    itemType: 'cocktail_table',
    displayName: 'Cocktail Table',
    category: 'feature_zone',
    defaultSeats: null,
    maxSeats: null,
    defaultWidthFt: 2.5,
    defaultHeightFt: 2.5,
    iconKey: 'cocktail_table',
    hasStockLimit: true,
  },

  // --- Standalone chair (added: production update milestone) ---
  // 1.5ft × 1.5ft footprint. The visual radius in the editor/SVG is
  // defaultWidthFt * FEET_TO_PX / 2 — i.e., 0.75ft in feet, 9px at scale=12.
  // has_stock_limit=false: chairs are tracked via the global chair_stock pool,
  // not per catalog object type. Standalone chairs still count toward the
  // total chair usage (via seats=1) that is compared against chair_stock.
  // ⚠️  Confirm 1.5ft dimension with venue if chairs differ from standard.
  {
    itemType: 'standalone_chair' as const,
    displayName: 'Chair',
    category: 'guest_seating' as const,
    defaultSeats: 1,
    maxSeats: 1,
    defaultWidthFt: 1.5,
    defaultHeightFt: 1.5,
    iconKey: 'standalone_chair',
    hasStockLimit: false,
  },
  // --- Arbor (added: production update milestone) ---
  // Generic ceremony arch placement marker. No inventory enforcement.
  // Dimensions isolated in ARBOR_WIDTH_FT / ARBOR_DEPTH_FT constants above.
  // ⚠️  Confirm 6ft × 2ft with venue.
  {
    itemType: 'arbor' as const,
    displayName: 'Arbor',
    category: 'feature_zone' as const,
    defaultSeats: null,
    maxSeats: null,
    defaultWidthFt: ARBOR_WIDTH_FT,
    defaultHeightFt: ARBOR_DEPTH_FT,
    iconKey: 'arbor',
    hasStockLimit: false,
  },
  {
    itemType: 'text_note',
    displayName: 'Text Note',
    category: 'annotation',
    defaultSeats: null,
    maxSeats: null,
    defaultWidthFt: 4,
    defaultHeightFt: 1.5,
    iconKey: 'text_note',
    hasStockLimit: false,
  },
];

export const DEFAULT_INVENTORY_LIMITS: InventoryLimit[] = [
  { itemType: 'round_table', maxQuantity: 25 }, // PLACEHOLDER — confirm real count
  { itemType: 'rect_table_6', maxQuantity: 10 }, // PLACEHOLDER — confirm real count
  { itemType: 'rect_table_8', maxQuantity: 8 }, // PLACEHOLDER — confirm real count
  { itemType: 'dance_floor_15', maxQuantity: 1 },
  { itemType: 'dance_floor_18', maxQuantity: 1 },
  { itemType: 'cocktail_table', maxQuantity: 12 }, // PLACEHOLDER — confirm real count
];

export function getCatalogEntry(
  itemType: string,
  catalog: ObjectCatalogEntry[] = DEFAULT_OBJECT_CATALOG
): ObjectCatalogEntry {
  const entry = catalog.find((c) => c.itemType === itemType);
  if (!entry) {
    throw new Error(`No catalog entry found for item type "${itemType}"`);
  }
  return entry;
}

/** White chairs are tracked as a single venue-wide number, not a catalog
 * object type (chairs are rendered attached to tables, never placed
 * individually — PRD §6, §9.3). Stored separately for clarity. */
export const CHAIR_STOCK = 200;


